// @atlas/contracts — a `payment-cards` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { nullableStr } from '../_shared/schemas.js';
/** A bankkártya-típus (a Schema Registry enum-ja tükrözve). */
export const PaymentCardBrandSchema = z.enum(['visa', 'mastercard', 'amex', 'maestro', 'other']);
/** Bankkártya write-body (create + update közös törzs). A `holderName` kötelező; a `last4` pontosan 4 számjegy;
 *  a `expiryMonth` 1–12, a `expiryYear` 2000–2100 (coerce int — a form string-ből küldi). */
export const PaymentCardWriteBodySchema = z.object({
    holderName: z.string().trim().min(1).max(255),
    brand: PaymentCardBrandSchema,
    last4: z.string().regex(/^\d{4}$/),
    expiryMonth: z.coerce.number().int().min(1).max(12),
    expiryYear: z.coerce.number().int().min(2000).max(2100),
    bankName: nullableStr(255),
    note: nullableStr(2000),
});
/** Create-body: + cél-ügyfél (CSAK create-nél — update/delete a kártya-sor tenantjából dolgozik). */
export const PaymentCardCreateBodySchema = PaymentCardWriteBodySchema.extend({
    clientId: z.string().uuid(),
});
/** Egy bankkártya sora (lista + detail közös alak). A `clientName` a foundation `/v1/admin/clients` feloldásból. */
export const PaymentCardRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** A tulajdonos ügyfél neve (feloldva) — a lista-soron látszik. */
    clientName: z.string().nullable(),
    holderName: z.string().nullable(),
    brand: z.string().nullable(),
    last4: z.string().nullable(),
    expiryMonth: z.number().nullable(),
    expiryYear: z.number().nullable(),
    bankName: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * A kártyát HASZNÁLÓ szolgáltatás-hozzárendelések (a legacy lista 5. oszlopa: „Hozzárendelések"). Miért
     * kell: törlés/csere előtt látni kell, mi függ a kártyától — a legacy ezért írta ki a scope-ot is
     * („ügyfél szintű" / „fiók szintű") és az érintett ügyfél/fiók nevét.
     */
    assignments: z.array(z.object({
        id: z.string(),
        /** A szolgáltatás (vagy csomag) feloldott neve; feloldatlan → null. */
        serviceName: z.string().nullable(),
        /** `client` | `account` — a legacy scope-címkéi. */
        scope: z.string(),
        /** A scope szerinti tulajdonos NEVE (fiók-szintűnél a fiók, ügyfél-szintűnél az ügyfél). */
        ownerName: z.string().nullable(),
    })),
    /** OCC-verzió (a busz-sor `version`-je) — a szerkesztés/törlés If-Match forrása. */
    version: z.number(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
/** Bankkártya lista-lekérdezés: szabad-szöveg (birtokos/bank/utolsó4) + ügyfél-szűrő + szerver-rendezés
 *  (whitelist) + offset-lapozás (a BFF-szűrt halmazon). A státusz (érvényes/lejár/lejárt) TISZTÁN kliens-oldalon
 *  számított a lejáratból — nincs hozzá szerver-szűrő. */
export const PaymentCardListQuerySchema = z.object({
    q: z.string().optional(),
    clientId: z.string().uuid().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** A create-form ügyfél-választójának refs-adata (foundation `/v1/admin/clients`, id + név). */
export const PaymentCardClientRefsSchema = z.object({
    clients: z.array(z.object({ id: z.string(), name: z.string() })),
});
//# sourceMappingURL=schemas.js.map